import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Clock, Calendar, ClipboardList, ChevronRight } from 'lucide-react';
import AsaAssistant from '@/components/shared/AsaAssistant';
import { Link } from 'react-router-dom';

export default function ProductionDashboardMain() {
  const { member } = useSession();
  
  const { clientPOs, isLoading: loadingPOs } = useClientPOs();
  
  const { data: productionTasks = [], isLoading: loadingTasks } = useQuery({ 
    queryKey: ['production-tasks'], 
    queryFn: () => base44.entities.ProductionTask.list().catch(() => []) 
  });

  const { data: productionOrders = [] } = useQuery({ 
    queryKey: ['production-orders'], 
    queryFn: () => base44.entities.ProductionOrder.list().catch(() => []) 
  });

  const activePOs = useMemo(() => {
    return clientPOs.filter(po => {
      const isValidStatus = !['pending', 'batal', 'pesanan selesai'].includes(po.status?.toLowerCase());
      const isPaid = po.status_tagihan === 'lunas' || po.status_tagihan === 'dp_terbayar';
      return isValidStatus && isPaid && po.statusProduksi !== 'PESANAN SELESAI' && po.statusProduksi !== 'BATAL';
    });
  }, [clientPOs]);

  // Asa Insights for Production
  const asaInsights = useMemo(() => {
    const insights = [];
    const now = new Date();

    // Check for completed Maserasi in Production Orders
    productionOrders.forEach(po => {
      if (po.status === 'maserasi' && po.maserasi_start) {
        const start = new Date(po.maserasi_start).getTime();
        const daysMs = (po.maserasi_durasi_hari || 0) * 24 * 60 * 60 * 1000;
        const hoursMs = (po.maserasi_durasi_jam || 0) * 60 * 60 * 1000;
        const target = start + daysMs + hoursMs;
        
        if (now.getTime() >= target) {
          insights.push({
            id: `maserasi-done-${po.id}`,
            type: 'success',
            text: `Yeay! Maserasi untuk PO **${po.brand_name?.split('/')[0] || '#' + po.order_number}** sudah selesai nih! Yuk, segera lanjut ke proses Filling agar produksinya nggak terhambat! ✨`
          });
        }
      } else if (po.status === 'menunggu_filling') {
          insights.push({
            id: `menunggu-filling-${po.id}`,
            type: 'warning',
            text: `Proses Maserasi untuk PO **${po.brand_name?.split('/')[0] || '#' + po.order_number}** sudah selesai dan sedang Menunggu Filling. Segera jadwalkan tim untuk mengisi ke dalam botol ya!`
          });
      }
    });

    activePOs.forEach(po => {
      // Check Deadline
      if (po.deadline) {
        const deadlineDate = new Date(po.deadline);
        const daysToDeadline = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
        
        if (daysToDeadline < 0) {
          insights.push({
            id: `po-overdue-${po.id}`,
            type: 'alert',
            text: `Gawat! PO **#${po.id}** (${po.customerName}) sudah MELEWATI deadline (${daysToDeadline * -1} hari lalu). Tolong segera dikebut dan kabari marketing ya!`
          });
        } else if (daysToDeadline <= 7) {
          insights.push({
            id: `po-deadline-${po.id}`,
            type: 'warning',
            text: `PO **#${po.id}** sisa waktu produksinya tinggal **${daysToDeadline} hari** lagi. Pastikan semua *bottleneck* sudah diatasi agar tidak telat kirim.`
          });
        }
      }

      const status = po.statusProduksi?.toUpperCase() || '';
      const createdDate = new Date(po.date || po.created_date || now);
      const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));

      if (['MENUNGGU PRODUKSI', 'ANTRIAN PRODUKSI'].includes(status) && daysSinceCreated > 5) {
        insights.push({
          id: `po-stuck-${po.id}`,
          type: 'warning',
          text: `PO **#${po.id}** kok masih '*Menunggu Produksi*' padahal udah masuk sejak ${daysSinceCreated} hari lalu? Ayo segera dinaikin ke jalur produksi!`
        });
      }
    });

    return insights;
  }, [activePOs, productionOrders]);

  const myTasks = useMemo(() => {
    return productionTasks.filter(t => t.assignee_id === member?.id && t.status !== 'Selesai');
  }, [productionTasks, member]);

  return (
    <div className="space-y-3 md:space-y-8 pb-16 text-[#302621]">
      <div className="flex flex-col gap-0 md:gap-1">
        <h1 className="text-[13px] md:text-3xl font-black text-slate-800 tracking-tight leading-tight">Dashboard Utama Produksi</h1>
        <p className="text-[6.5px] md:text-xs text-slate-500 leading-relaxed mt-0.5 md:mt-0">
          Semangat kerja, {member?.full_name || 'Tim Produksi'}! Pantau terus *deadline* dan selesaikan tugas-tugas di bawah ini.
        </p>
      </div>

      {loadingPOs ? (
        <div className="animate-pulse space-y-4">
          <div className="h-40 bg-slate-100 rounded-3xl"></div>
        </div>
      ) : (
        <AsaAssistant 
          insights={asaInsights} 
          memberName={member?.full_name?.split(' ')[0]} 
          title="Asa Production Analyst"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
        
        {/* Panel Tugas Saya */}
        <div>
          <div className="flex items-center justify-between mb-1.5 md:mb-4">
            <h3 className="text-[10px] md:text-xl font-black text-slate-800 tracking-tight flex items-center gap-1 md:gap-2">
              <ClipboardList className="w-3 h-3 md:w-5 md:h-5 text-[#8C6208]" /> Tugas Anda
            </h3>
            <Link to="/office/production/tasks" className="text-[6px] md:text-xs font-bold text-[#8C6208] hover:underline">
              Lihat Semua
            </Link>
          </div>

          <div className="space-y-3">
            {loadingTasks ? (
               <div className="h-20 bg-slate-100 animate-pulse rounded-xl"></div>
            ) : myTasks.length === 0 ? (
               <div className="flex flex-col items-center justify-center py-5 md:py-10 bg-slate-50/50 rounded-lg md:rounded-2xl border border-dashed border-slate-200">
                 <CheckCircle2 className="w-4 h-4 md:w-10 md:h-10 mb-1 md:mb-2 text-emerald-400" />
                 <p className="text-[7px] md:text-sm font-semibold text-slate-500">Tidak ada tugas aktif.</p>
               </div>
            ) : (
               myTasks.map(task => (
                 <Card key={task.id} className="border border-slate-200/80 hover:border-[#8C6208]/40 transition-all bg-white shadow-sm">
                   <CardContent className="p-3 flex items-center gap-3">
                     <div className="p-2 bg-amber-50 rounded-xl">
                       <Clock className="w-3 h-3 md:w-5 md:h-5 text-[#8C6208]" />
                     </div>
                     <div className="flex-1 min-w-0">
                       <h4 className="font-bold text-slate-800 truncate">{task.title || 'Tugas Produksi'}</h4>
                       <p className="text-xs text-slate-500 truncate">{task.po_reference ? `Untuk PO #${task.po_reference}` : 'Tugas Umum'}</p>
                     </div>
                     <Link to="/office/production/tasks" className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold transition-colors">
                       Kerjakan
                     </Link>
                   </CardContent>
                 </Card>
               ))
            )}
          </div>
        </div>

        {/* Panel PO Berjalan */}
        <div>
          <div className="flex items-center justify-between mb-1.5 md:mb-4 mt-3 md:mt-0">
            <h3 className="text-[10px] md:text-xl font-black text-slate-800 tracking-tight flex items-center gap-1 md:gap-2">
              <Calendar className="w-3 h-3 md:w-5 md:h-5 text-[#8C6208]" /> Menunggu Produksi
            </h3>
            <Link to="/office/production/po-list" className="text-[6px] md:text-xs font-bold text-[#8C6208] hover:underline">
              Semua PO
            </Link>
          </div>

          <div className="space-y-3">
            {activePOs.slice(0, 5).map(po => {
               const status = po.statusProduksi || 'MENUNGGU PRODUKSI';
               const linkedPO = productionOrders.find(p => p.invoice_id === po.id || p.order_number === po.id);
               const isUpdated = linkedPO?.is_po_updated || false;
               
               return (
                 <Link to={`/office/production/${po.id}`} key={po.id}>
                   <Card className="group hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer border-[#E6D5B8]/30 overflow-hidden bg-white mb-3">
                     <CardContent className="p-4 flex items-center justify-between">
                        <div className="min-w-0 pr-4">
                          <div className="flex items-center gap-2 mb-1">
                             <span className="text-[10px] text-slate-400 font-mono font-bold">#{po.id}</span>
                             <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200">
                               {status}
                             </span>
                             {isUpdated && (
                               <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border bg-rose-50 text-rose-600 border-rose-200">
                                 UPDATE
                               </span>
                             )}
                          </div>
                          <h3 className="font-display font-bold text-[#302621] truncate">{po.customerName?.split('/')[0]}</h3>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-200 group-hover:text-[#8C6208] transition-colors shrink-0" />
                     </CardContent>
                   </Card>
                 </Link>
               );
            })}
            
            {activePOs.length === 0 && (
               <div className="flex flex-col items-center justify-center py-5 md:py-10 bg-slate-50/50 rounded-lg md:rounded-2xl border border-dashed border-slate-200">
                 <p className="text-[7px] md:text-sm font-semibold text-slate-500">Tidak ada PO yang sedang berjalan.</p>
               </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
